import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { MEMORY_KIND_LABELS, type MemberRole, type MemoryItem, type Project, type StateDoc } from '@jabberr/core';
import { Markdown } from '../components/Markdown.js';
import { ErrorBox, Spinner } from '../components/ui.js';
import { api, errorMessage } from '../lib/api.js';
import { longDate, shortDate } from '../lib/format.js';
import { liveVersion } from '../lib/plain.js';
import { activeMemory, contextBullets, docSections, useKnows } from '../state/knows.js';
import { useDrawer } from '../state/drawer.js';
import { BackIcon } from '../components/icons.js';

/**
 * What Jabberr knows, in the order it trusts it: what is live, what the team decided and plans, and what it
 * remembers. One page over the Context, State and Memory pages, which stay as the places to edit each part.
 */
export function KnowsPage() {
  const { projectId = '' } = useParams();
  const { setInfo } = useDrawer();
  const [project, setProject] = useState<Project | null>(null);
  const [role, setRole] = useState<MemberRole | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const guest = role === 'guest';
  // Wait for the role, so a guest never asks for team knowledge and the team never sees a half-loaded page.
  const knows = useKnows(role === undefined ? '' : projectId, { team: !guest });

  useEffect(() => {
    let alive = true;
    api.projects
      .get(projectId)
      .then((r) => {
        if (!alive) return;
        setProject(r.project);
        setRole(r.role ?? 'member');
      })
      .catch((err) => alive && setError(errorMessage(err)));
    setInfo({ projectId, title: 'What Jabberr knows' });
    return () => {
      alive = false;
    };
  }, [projectId, setInfo]);

  const forget = async (item: MemoryItem): Promise<void> => {
    try {
      const { item: updated } = await api.memory.update(item.id, { status: 'retired' });
      if (knows.memory) {
        const swap = (list: MemoryItem[]) => list.map((i) => (i.id === updated.id ? updated : i));
        knows.setMemory({ project: swap(knows.memory.project), user: swap(knows.memory.user) });
      }
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const keep = async (item: MemoryItem): Promise<void> => {
    try {
      const { item: updated } = await api.memory.update(item.id, { status: 'active' });
      if (knows.memory) {
        const swap = (list: MemoryItem[]) => list.map((i) => (i.id === updated.id ? updated : i));
        knows.setMemory({ project: swap(knows.memory.project), user: swap(knows.memory.user) });
      }
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const production = knows.state?.production ?? null;
  const loading = role === undefined || knows.loading;

  return (
    <div className="page knows-page">
      <header className="knows-head">
        <Link to={`/p/${projectId}`} className="back-link">
          <BackIcon />
          {project?.name ?? 'Project'}
        </Link>
        <h1>What Jabberr knows</h1>
        <p className="muted">
          {guest
            ? 'What Jabberr uses when it talks with you: what is live today and what you have told it.'
            : 'Three places, in the order Jabberr trusts them. When they disagree about what exists, the one further left wins.'}
        </p>
      </header>
      {error ? <ErrorBox>{error}</ErrorBox> : null}
      {loading ? (
        <Spinner label="Loading" />
      ) : (
        <div className={`knows-columns ${guest ? 'knows-columns-guest' : ''}`}>
          <LiveColumn projectId={projectId} doc={production} guest={guest} />
          {guest ? null : (
            <>
              <PlansColumn projectId={projectId} knows={knows} onKeep={keep} onForget={forget} />
              <RemembersColumn projectId={projectId} memory={knows.memory} onForget={forget} />
            </>
          )}
        </div>
      )}
      {!loading && !guest ? (
        <Link to={`/p/${projectId}/outputs`} className="knows-docs-link">
          <span>Saved documents</span>
          <span className="muted">{knows.outputs ? `${knows.outputs.length}` : ''}</span>
        </Link>
      ) : null}
      {!loading && !guest ? (
        <Link to={`/p/${projectId}/rated`} className="knows-docs-link">
          <span>Rated replies</span>
          <span className="muted">Thumbs up and down</span>
        </Link>
      ) : null}
    </div>
  );
}

function ColumnHead({ n, title, tag, strong }: { n: number; title: string; tag: string; strong?: boolean }) {
  return (
    <div className="knows-col-head">
      <h2>
        {n}. {title}
      </h2>
      <span className={`knows-tag ${strong ? 'strong' : ''}`}>{tag}</span>
    </div>
  );
}

function LiveColumn({ projectId, doc, guest }: { projectId: string; doc: StateDoc | null; guest: boolean }) {
  const version = liveVersion(doc?.content);
  const parts = doc ? docSections(doc.content) : null;
  return (
    <section className="knows-col" aria-label="What's live now">
      <ColumnHead n={1} title="What's live now" tag="Customers see this" strong />
      {doc && parts ? (
        <>
          <p className="knows-lede">
            Sent after every release.{version ? ` Version ${version},` : ''} {longDate(doc.createdAt)}.
          </p>
          {parts.sections.length > 0 ? (
            <div className="knows-items">
              {parts.lede ? <div className="knows-item soft"><Markdown text={parts.lede} /></div> : null}
              {parts.sections.map((s) => (
                <div key={s.title} className="knows-item soft">
                  <span className="knows-item-title">{s.title}</span>
                  {s.body ? <div className="knows-item-body"><Markdown text={s.body} /></div> : null}
                </div>
              ))}
            </div>
          ) : (
            <div className="knows-item soft">
              <Markdown text={doc.content} />
            </div>
          )}
        </>
      ) : (
        <p className="knows-lede">Nothing has been sent yet. Each release sends this; a person can also write it by hand.</p>
      )}
      {guest ? null : (
        <div className="knows-col-foot">
          <Link to={`/p/${projectId}/state`}>Edit what's live and being built</Link>
          <Link to={`/p/${projectId}/context?layer=client`}>What clients are told</Link>
        </div>
      )}
    </section>
  );
}

function PlansColumn({
  projectId,
  knows,
  onKeep,
  onForget,
}: {
  projectId: string;
  knows: ReturnType<typeof useKnows>;
  onKeep: (i: MemoryItem) => Promise<void>;
  onForget: (i: MemoryItem) => Promise<void>;
}) {
  const fromNotes = contextBullets(knows.context?.content, 'Decisions');
  const decisions = activeMemory(knows.memory?.project).filter((i) => i.kind === 'decision');
  const dev = knows.state?.dev ?? null;
  const backlog = knows.state?.backlog ?? null;
  const empty = fromNotes.length === 0 && decisions.length === 0 && !dev && !backlog;
  return (
    <section className="knows-col" aria-label="Decisions and plans">
      <ColumnHead n={2} title="Decisions and plans" tag="Team only" />
      <p className="knows-lede">What people said yes to and what is being built. Every new conversation starts from these.</p>
      <div className="knows-items">
        {fromNotes.map((text) => (
          <div key={`n-${text}`} className="knows-item">
            <span className="knows-item-title">{text}</span>
            <span className="knows-item-meta">Project notes{knows.context ? ` · ${shortDate(knows.context.publishedAt)}` : ''}</span>
          </div>
        ))}
        {decisions.map((i) =>
          i.status === 'proposed' ? (
            <div key={i.id} className="knows-item pending">
              <span className="knows-item-title">{i.text}</span>
              <span className="knows-item-meta">Heard in a conversation · {shortDate(i.createdAt)} · not confirmed yet</span>
              <span className="knows-item-actions">
                <button type="button" className="btn btn-small" onClick={() => void onKeep(i)} aria-label={`Keep ${i.text}`}>
                  Keep
                </button>
                <button type="button" className="btn btn-small" onClick={() => void onForget(i)} aria-label={`Forget ${i.text}`}>
                  Forget
                </button>
              </span>
            </div>
          ) : (
            <div key={i.id} className="knows-item">
              <span className="knows-item-title">{i.text}</span>
              <span className="knows-item-meta">
                {i.source === 'manual' ? 'Added by hand' : 'From a conversation'} · {shortDate(i.createdAt)}
              </span>
            </div>
          ),
        )}
        {dev ? <DocCard title="Being built" doc={dev} /> : null}
        {backlog ? <DocCard title="Planned" doc={backlog} /> : null}
        {empty ? <p className="muted small">Nothing decided yet. Decisions you save from a conversation show up here.</p> : null}
      </div>
      <div className="knows-col-foot">
        <Link to={`/p/${projectId}/context`}>Edit the project notes</Link>
      </div>
    </section>
  );
}

function DocCard({ title, doc }: { title: string; doc: StateDoc }) {
  const { lede, sections } = docSections(doc.content);
  const summary = lede || sections[0]?.body || doc.content;
  return (
    <div className="knows-item soft">
      <span className="knows-item-title">{title}</span>
      <span className="knows-item-body clamp">
        <Markdown text={summary} />
      </span>
      <span className="knows-item-meta">Updated {shortDate(doc.createdAt)}</span>
    </div>
  );
}

function RemembersColumn({ projectId, memory, onForget }: { projectId: string; memory: ReturnType<typeof useKnows>['memory']; onForget: (i: MemoryItem) => Promise<void> }) {
  const project = activeMemory(memory?.project).filter((i) => i.kind !== 'decision');
  const mine = activeMemory(memory?.user);
  return (
    <section className="knows-col" aria-label="What Jabberr remembers">
      <ColumnHead n={3} title="What Jabberr remembers" tag="Team only" />
      <p className="knows-lede">Ideas, reasons and people, picked up from conversations and meetings.</p>
      <ul className="knows-items" aria-label="Memory">
        {[...project, ...mine].map((i) => (
          <li key={i.id} className={`knows-item row-item ${i.status === 'proposed' ? 'pending' : ''}`}>
            <span className="knows-item-text">
              <span>{i.text}</span>
              <span className="knows-item-meta">
                {i.scope === 'user' ? 'About you · only you see this' : `${MEMORY_KIND_LABELS[i.kind].replace(/s$/, '')} · ${i.source === 'manual' ? 'added by hand' : 'from a conversation'} · ${shortDate(i.createdAt)}`}
              </span>
            </span>
            <button type="button" className="btn btn-small" onClick={() => void onForget(i)} aria-label={`Forget ${i.text}`}>
              Forget
            </button>
          </li>
        ))}
        {project.length + mine.length === 0 ? <li className="muted small">Nothing remembered yet.</li> : null}
      </ul>
      <div className="knows-col-foot">
        <Link to={`/p/${projectId}/memory`}>Add, fix or bring back a note</Link>
      </div>
    </section>
  );
}
